import { DocumentationArticle } from '../components/DocumentationArticle';

export const NavigatingDashboard = () => {
  return (
    <DocumentationArticle
      title="Navigating the EchoRatings Dashboard"
      description="This guide helps you understand the main areas of the EchoRatings dashboard so you know where to find information and take action without digging through menus."
      timeToComplete="3-5 minutes"
      requirements={['Access to your EchoRatings account']}
      sections={[
        {
          title: 'Section 1: Access the Dashboard',
          content: (
            <>
              <ol className="list-decimal list-inside space-y-2 mb-4">
                <li>Log in to your EchoRatings account.</li>
                <li>
                  After signing in, you will land on the dashboard automatically.
                </li>
              </ol>
              <p>
                If you navigate away at any time, you can return to the dashboard
                from the main navigation.
              </p>
            </>
          ),
        },
        {
          title: 'Section 2: Understand the Main Dashboard Areas',
          content: (
            <>
              <p className="mb-4">
                The dashboard is organized to give you a clear snapshot of what's
                happening with your reviews.
              </p>
              <p className="mb-2">You'll typically see:</p>
              <ul className="list-disc list-inside space-y-1 mb-4">
                <li>A summary of recent review activity</li>
                <li>The connection status of your review platforms</li>
                <li>Quick access to common actions</li>
              </ul>
              <p>
                This information is meant to help you stay oriented, not to provide
                detailed analysis.
              </p>
            </>
          ),
        },
        {
          title: 'Section 3: Review Activity Overview',
          content: (
            <>
              <p className="mb-4">
                The review activity area shows incoming reviews and recent updates.
              </p>
              <p className="mb-2">Here you can:</p>
              <ol className="list-decimal list-inside space-y-2 mb-4">
                <li>See when new reviews arrive.</li>
                <li>Identify which platform a review came from.</li>
                <li>Open individual reviews to read or respond.</li>
              </ol>
              <p>
                You do not need to configure anything in this section for it to
                work.
              </p>
            </>
          ),
        },
        {
          title: 'Section 4: Platform Connection Status',
          content: (
            <>
              <p className="mb-4">
                Another part of the dashboard shows whether your review platforms
                are connected.
              </p>
              <p className="mb-2">This helps you:</p>
              <ul className="list-disc list-inside space-y-1 mb-4">
                <li>Confirm that platforms are connected correctly.</li>
                <li>Spot missing or disconnected platforms early.</li>
              </ul>
              <p>
                If a platform is not connected, you can go to the integrations area
                to complete setup.
              </p>
            </>
          ),
        },
        {
          title: 'Section 5: Using the Dashboard as a Starting Point',
          content: (
            <>
              <p className="mb-4">
                The dashboard is designed to act as a starting point for common
                tasks.
              </p>
              <p className="mb-2">From here, you can:</p>
              <ol className="list-decimal list-inside space-y-2 mb-4">
                <li>Check recent review activity.</li>
                <li>Access settings and integrations.</li>
                <li>Navigate to automations or account-related areas.</li>
              </ol>
              <p>
                Think of the dashboard as your home base rather than a place for
                deep configuration.
              </p>
            </>
          ),
        },
      ]}
      faq={[
        {
          question: 'Can I customize what I see on the dashboard?',
          answer: 'At this stage, the dashboard layout is fixed.',
        },
        {
          question: 'Is the dashboard updated automatically?',
          answer:
            'Yes. Review activity and connection status update as data syncs.',
        },
        {
          question: 'Do I need to check the dashboard every day?',
          answer:
            'That depends on how often you want to monitor reviews. There is no required frequency.',
        },
      ]}
    />
  );
};
